import { useForm } from "@inertiajs/react";
import { GuestCheckoutModal } from "@/components/ProductShowView/components/GuestCheckoutModal";
import { type PurchaseSuccess, PurchaseSuccessModal } from "@/components/PurchaseSuccess";
import { RoutedModal } from "@/components/RoutedModal";
import { firstError } from "@/lib/forms/first-error";

interface GuestPurchaseFlowProps {
  free: boolean;
  kind: "product" | "service";
  /** POST target for the purchase (product or service purchases path). */
  path: string;
  /** Set after a free purchase redirects back to this modal route. */
  success: PurchaseSuccess | null;
}

/**
 * The guest email-capture → free-success flow shared by the product and service
 * purchase modal routes. A free purchase redirects back to the same modal route
 * with `success` set, so the modal stays mounted and swaps to the success state
 * in place (the magic-link flash-then-redirect pattern).
 */
export function GuestPurchaseFlow({ free, kind, path, success }: GuestPurchaseFlowProps) {
  const form = useForm<{ email: string }>({ email: "" });

  return (
    <RoutedModal size="sm">
      {({ close }) =>
        success ? (
          <PurchaseSuccessModal {...success} onClose={close} />
        ) : (
          <GuestCheckoutModal
            email={form.data.email}
            emailError={firstError(form.errors.email)}
            free={free}
            isPending={form.processing}
            kind={kind}
            onClose={close}
            onEmailChange={(value) => form.setData("email", value)}
            onSubmit={() => {
              form.post(path, { preserveScroll: "errors", preserveState: true });
            }}
          />
        )
      }
    </RoutedModal>
  );
}
